import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Form, Alert, Spinner, Image } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useProfileImage, DEFAULT_AVATAR_URL } from '../context/ProfileImageContext';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = url;
  });

async function getCroppedImageBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas blob conversion failed'))),
      'image/jpeg',
      0.92
    );
  });
}

const FIRST_NAME_RE = /^[A-Za-z]+$/;
const LAST_NAME_RE = /^[A-Za-z ]+$/;

function validateFirstName(value) {
  if (!FIRST_NAME_RE.test(value)) {
    return 'First name may contain letters only — no spaces, digits, or symbols.';
  }
  if (value.length < 2) return 'First name must be at least 2 letters.';
  if (value.length > 50) return 'First name cannot exceed 50 characters.';
  return null;
}

function validateLastName(value) {
  if (!LAST_NAME_RE.test(value)) {
    return 'Last name may contain letters and spaces only — no digits or symbols.';
  }
  if (value.length > 50) return 'Last name cannot exceed 50 characters.';
  const letters = (value.match(/[A-Za-z]/g) || []).length;
  if (letters < 3) return 'Last name must contain at least 3 letters.';
  return null;
}

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { imageUrl, loading: imageLoading, refresh } = useProfileImage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const userId = user?.id || user?._id;

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [fieldErrors, setFieldErrors] = useState({});
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Crop state
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
  }, [user]);

  // /auth/login returns a concatenated `name` but no firstName/lastName, so on
  // a fresh-login session those fields are missing from AuthContext until the
  // next /auth/verify. Fetch the canonical record once if they're absent.
  useEffect(() => {
    if (!userId) return;
    if (user?.firstName && user?.lastName) return;

    let cancelled = false;
    axios
      .get(`/users/getUserById/${userId}`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        updateUser({
          firstName: res.data?.firstName,
          lastName: res.data?.lastName
        });
      })
      .catch((err) => {
        console.warn('Could not load user details:', err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, user?.firstName, user?.lastName, updateUser]);

  const onCropComplete = useCallback((_croppedArea, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const triggerFileSelect = () => fileInputRef.current?.click();

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setSuccess('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawImageSrc(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const exitCropMode = () => {
    setRawImageSrc(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const trimmedFirst = firstName.trim();
  const trimmedLast = lastName.trim();
  const firstNameDirty = trimmedFirst !== (user?.firstName || '');
  const lastNameDirty = trimmedLast !== (user?.lastName || '');
  const hasNameChanges = firstNameDirty || lastNameDirty;
  const hasImageChange = !!rawImageSrc && !!croppedAreaPixels;
  const hasPendingChanges = hasNameChanges || hasImageChange;

  const handleSaveChanges = async () => {
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Client-side validation only on fields the user actually changed.
    const localErrors = {};
    if (firstNameDirty) {
      const err = validateFirstName(trimmedFirst);
      if (err) localErrors.firstName = err;
    }
    if (lastNameDirty) {
      const err = validateLastName(trimmedLast);
      if (err) localErrors.lastName = err;
    }
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    setSaving(true);
    const messages = [];
    try {
      if (hasNameChanges) {
        const payload = {};
        if (firstNameDirty) payload.firstName = trimmedFirst;
        if (lastNameDirty) payload.lastName = trimmedLast;
        const response = await axios.patch(`/users/updateUser/${userId}`, payload, {
          withCredentials: true
        });
        const newFirst = response.data.firstName;
        const newLast = response.data.lastName;
        updateUser({
          firstName: newFirst,
          lastName: newLast,
          name: `${newFirst} ${newLast}`
        });
        messages.push('Name updated.');
      }

      if (hasImageChange) {
        const blob = await getCroppedImageBlob(rawImageSrc, croppedAreaPixels);
        const formData = new FormData();
        formData.append('image', blob, 'profile.jpg');
        await axios.post(`/users/${userId}/profile-image`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        await refresh();
        exitCropMode();
        messages.push('Profile image saved.');
      }

      if (messages.length > 0) {
        setSuccess(messages.join(' '));
      }
    } catch (err) {
      console.error('Save failed:', err);
      const status = err.response?.status;
      const data = err.response?.data;

      // 422 from validate middleware: { errors: [{ firstName: 'msg' }, ...] }
      if (status === 422 && Array.isArray(data?.errors)) {
        const fe = {};
        data.errors.forEach((entry) => {
          const [k, v] = Object.entries(entry)[0] || [];
          if (k) fe[k] = v;
        });
        setFieldErrors(fe);
        if (!fe.firstName && !fe.lastName) {
          setError(data?.error || 'Validation failed.');
        }
      // 400 from Mongoose ValidationError: { error, details: { firstName: 'msg' } }
      } else if (status === 400 && data?.details && typeof data.details === 'object') {
        setFieldErrors(data.details);
      } else {
        setError(data?.error || 'Failed to save changes.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove your profile image?')) return;
    setError('');
    setSuccess('');
    setRemoving(true);
    try {
      await axios.delete(`/users/${userId}/profile-image`, { withCredentials: true });
      await refresh();
      setSuccess('Profile image removed.');
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.response?.data?.error || 'Failed to remove profile image.');
    } finally {
      setRemoving(false);
    }
  };

  const handleBack = () => {
    if (hasPendingChanges && !window.confirm('You have unsaved changes. Discard and leave?')) return;
    navigate('/dashboard');
  };

  const inCropMode = !!rawImageSrc;

  return (
    <div className="content-container">
      <h3 className="mb-1">My Personal Details</h3>
      <p className="text-muted small mb-0">
        Manage your profile information and account settings.
      </p>

      <Container fluid className="mt-4">
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

        <Row>
          <Col md={4} className="text-center mb-4">
            {inCropMode ? (
              <>
                <div className="profile-cropper-container mb-3">
                  <Cropper
                    image={rawImageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="rect"
                    showGrid={false}
                    minZoom={1}
                    maxZoom={3}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <Form.Group className="mb-2 px-3">
                  <Form.Label className="small text-muted mb-1">Zoom</Form.Label>
                  <Form.Range
                    min={1}
                    max={3}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    disabled={saving}
                  />
                </Form.Group>
                <small className="text-muted d-block mb-2">
                  Drag to position. Scroll or use the slider to zoom.
                </small>
              </>
            ) : (
              <div className="profile-image-wrapper mb-3">
                {imageLoading ? (
                  <div className="profile-image-placeholder d-flex justify-content-center align-items-center">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <Image
                    src={imageUrl || DEFAULT_AVATAR_URL}
                    className="profile-image-large"
                    alt="Profile"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_AVATAR_URL;
                    }}
                  />
                )}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelected}
              hidden
            />

            <div className="d-flex flex-column align-items-center gap-2">
              <Button
                variant="primary"
                onClick={triggerFileSelect}
                disabled={saving || removing}
              >
                {inCropMode ? 'Choose Different Photo' : 'Upload New Photo'}
              </Button>
              {inCropMode && (
                <Button
                  variant="outline-secondary"
                  onClick={exitCropMode}
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
              {!inCropMode && imageUrl && (
                <Button
                  variant="outline-danger"
                  onClick={handleRemove}
                  disabled={saving || removing}
                >
                  {removing ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" className="me-2" />
                      Removing...
                    </>
                  ) : (
                    'Remove Photo'
                  )}
                </Button>
              )}
            </div>
            <small className="text-muted d-block mt-2">
              JPEG, PNG, WebP or GIF. Max 5&nbsp;MB.
            </small>
          </Col>

          <Col md={8}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" value={user?.email || ''} readOnly disabled />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    if (fieldErrors.firstName) {
                      setFieldErrors((prev) => ({ ...prev, firstName: undefined }));
                    }
                  }}
                  disabled={saving}
                  isInvalid={!!fieldErrors.firstName}
                  maxLength={50}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.firstName}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Letters only, 2&ndash;50 characters.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (fieldErrors.lastName) {
                      setFieldErrors((prev) => ({ ...prev, lastName: undefined }));
                    }
                  }}
                  disabled={saving}
                  isInvalid={!!fieldErrors.lastName}
                  maxLength={50}
                />
                <Form.Control.Feedback type="invalid">
                  {fieldErrors.lastName}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Letters and spaces only, max 50 characters, at least 3 letters.
                </Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={handleBack} disabled={saving}>
                  Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveChanges}
                  disabled={!hasPendingChanges || saving}
                  title={!hasPendingChanges ? 'Edit your name or upload a new photo to enable saving' : undefined}
                >
                  {saving ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Profile;
